import { useState, useEffect, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BarChart3, Building2, MapPin, Megaphone, Users, Layers, Calendar,
  ExternalLink, ChevronRight, ChevronDown, ArrowDown, Timer, RefreshCw, Target,
  AlertTriangle, Coins,
} from 'lucide-react';
import { getAdStats, getLocations, enrichAds } from '../api';
import { getApiErrorMessage } from '../utils/apiError';
import { formatDate, formatDuration } from '../utils/format';
import StatTile from '../components/ui/StatTile';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';

// Server-supported sort keys, surfaced as sortable table columns.
const SORTABLE = {
  recent: 'firstStarted',
  ads: 'adCount',
  reach: 'totalReach',
};

const DEFAULT_FILTERS = {
  page: 1,
  limit: 20,
  location: '',
  clinic: '',
  isActive: '',
  startDate: '',
  endDate: '',
  multiOnly: false,
  signal: '',
  sort: 'ads',
};

// How an event's advertising ended. 'stopped_early' is the one worth
// reviewing — but it says the ADS stopped, not that the event was cancelled.
const SIGNALS = {
  ongoing: { label: 'Still advertising', cls: 'badge-success' },
  ran_to_event: { label: 'Ran to event', cls: 'bg-ink-100 text-ink-600' },
  stopped_early: { label: 'Stopped early', cls: 'bg-amber-100 text-amber-700' },
  unknown: { label: 'Unknown', cls: 'bg-ink-100 text-ink-400' },
};

function AdStats() {
  // Deep links from elsewhere (e.g. the clinic page) pre-fill the filters.
  const [searchParams] = useSearchParams();
  const initialClinic = searchParams.get('clinic') || '';

  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({});
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, clinic: initialClinic });

  // Per-event enrichment progress, keyed by event id.
  const [enriching, setEnriching] = useState(() => new Set());
  const [enrichNote, setEnrichNote] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Draft inputs that only commit to `filters` on "Apply" (avoid a fetch per keystroke).
  const [clinicInput, setClinicInput] = useState(initialClinic);
  const [startInput, setStartInput] = useState('');
  const [endInput, setEndInput] = useState('');

  useEffect(() => {
    document.title = 'Ad Statistics | Dental Events';
    return () => { document.title = 'Dental Events'; };
  }, []);

  useEffect(() => {
    getLocations()
      .then((res) => setLocations(res.data.data))
      .catch((err) => console.error('Failed to load locations:', err));
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    // Only send params the API understands; drop empty strings.
    const params = { page: filters.page, limit: filters.limit, sort: filters.sort };
    if (filters.location) params.location = filters.location;
    if (filters.clinic) params.clinic = filters.clinic;
    if (filters.isActive) params.isActive = filters.isActive;
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.multiOnly) params.multiOnly = 'true';
    if (filters.signal) params.signal = filters.signal;

    getAdStats(params)
      .then((res) => {
        if (!active) return;
        setEvents(res.data.data.events);
        setSummary(res.data.data.summary);
        setPagination(res.data.pagination);
        setExpanded(new Set());
      })
      .catch((err) => { if (active) setError(getApiErrorMessage(err, 'Failed to load ad statistics')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [filters, reloadKey]);

  /**
   * Fetch audience data for every ad behind an event (or a whole clinic).
   * Costs an API credit per ad, so it is explicit rather than automatic.
   */
  const runEnrich = async (key, body, label) => {
    setEnriching((prev) => new Set(prev).add(key));
    setEnrichNote(null);
    try {
      const res = await enrichAds(body);
      const d = res.data.data;
      setEnrichNote({
        tone: 'ok',
        text: `${label}: ${d.enriched} ad${d.enriched === 1 ? '' : 's'} updated`
          + (d.withAudience ? `, ${d.withAudience} with audience data` : ', none carried audience data')
          + (d.skipped ? ` (${d.skipped} already current)` : '')
          + (d.creditsRemaining != null ? ` · ${d.creditsRemaining} credits left` : ''),
      });
      setReloadKey((k) => k + 1);
    } catch (err) {
      setEnrichNote({ tone: 'error', text: getApiErrorMessage(err, 'Could not update audience data') });
    } finally {
      setEnriching((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const applyFilters = () => {
    setFilters((f) => ({
      ...f,
      clinic: clinicInput.trim(),
      startDate: startInput,
      endDate: endInput,
      page: 1,
    }));
  };

  const setSort = (key) => setFilters((f) => ({ ...f, sort: key, page: 1 }));

  const toggleExpand = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const SortHeader = ({ sortKey, label, align = 'right', title }) => (
    <th
      onClick={() => setSort(sortKey)}
      title={title}
      className={`px-3 py-2.5 font-semibold whitespace-nowrap cursor-pointer select-none hover:text-ink-900 ${align === 'right' ? 'text-right' : 'text-left'} ${filters.sort === sortKey ? 'text-dental-700' : 'text-ink-600'}`}
    >
      {label} {filters.sort === sortKey && <ArrowDown className="w-3 h-3 inline" />}
    </th>
  );

  // Exact figures read strongest, coarse bands are muted, missing is greyed out.
  const reachTone = (source) =>
    source === 'total_reach' || source === 'reach_estimate'
      ? 'text-ink-900 font-semibold'
      : source === 'impressions' ? 'text-ink-500' : 'text-ink-300';

  return (
    <div className="space-y-5 sm:space-y-6 animate-fade-in-up">
      <div>
        <h1 className="page-title mb-1">Ad Statistics</h1>
        <p className="text-sm text-ink-500">
          Competitor ads by event — when each ad started and its impression volume. When several
          ads promote the same event, they are grouped so you can see every ad behind it.
          <span className="text-ink-400"> Reach is exact once an ad has been through
          &ldquo;Update&rdquo;; until then it falls back to Facebook&apos;s coarse impressions band,
          which is usually missing or reads &ldquo;&lt;100&rdquo;. Reach is never summed across ads —
          several ads from one clinic target the same places, so adding them counts the same people
          twice. &ldquo;Ran for&rdquo; is the median of an event&apos;s finished ads; a running ad
          shows a &ldquo;+&rdquo; because its span is still growing, and ads we have not re-crawled
          recently may still look live.</span>
        </p>
      </div>

      {/* KPI header */}
      <div className="grid grid-cols-2 lg:grid-cols-7 gap-3 sm:gap-4">
        <StatTile
          icon={Calendar}
          label="Events"
          value={summary?.totalEvents ?? '—'}
          subValue={summary ? `${summary.eventsWithAudience} with audience data` : undefined}
          tone="dental"
        />
        <StatTile icon={Layers} label="Multi-ad events" value={summary?.multiAdEvents ?? '—'} tone="accent" />
        <StatTile icon={Megaphone} label="Total ads" value={summary?.totalAds ?? '—'} tone="blue" />
        <StatTile icon={Building2} label="Active ads" value={summary?.activeAds ?? '—'} tone="green" />
        <StatTile
          icon={Timer}
          label="Median ad run"
          value={summary?.medianDurationDays != null ? formatDuration(summary.medianDurationDays) : '—'}
          subValue={summary ? `${summary.adsWithDuration} finished · ${summary.ongoingAds} running` : undefined}
          tone="ink"
        />
        <StatTile
          icon={Users}
          label="Ads w/ reach data"
          value={summary?.adsWithReach ?? '—'}
          subValue={summary ? `${summary.enrichedAds} exact` : undefined}
          tone="amber"
        />
        <StatTile
          icon={AlertTriangle}
          label="Stopped early"
          value={summary?.stoppedEarlyEvents ?? '—'}
          subValue="ads cut before the event"
          tone="red"
        />
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col lg:flex-row lg:flex-wrap gap-3 lg:items-end">
          <div className="flex-1 min-w-0 lg:min-w-[180px]">
            <label htmlFor="loc" className="block text-sm font-medium text-ink-600 mb-1">Location</label>
            <select
              id="loc"
              className="input w-full"
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value, page: 1 })}
            >
              <option value="">All locations</option>
              {locations.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-0 lg:min-w-[160px]">
            <label htmlFor="clinic" className="block text-sm font-medium text-ink-600 mb-1">Clinic</label>
            <input
              id="clinic"
              className="input w-full"
              placeholder="Clinic name…"
              value={clinicInput}
              onChange={(e) => setClinicInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            />
          </div>
          <div className="lg:w-40">
            <label htmlFor="active" className="block text-sm font-medium text-ink-600 mb-1">Ad status</label>
            <select
              id="active"
              className="input w-full"
              value={filters.isActive}
              onChange={(e) => setFilters({ ...filters, isActive: e.target.value, page: 1 })}
            >
              <option value="">All ads</option>
              <option value="true">Active only</option>
              <option value="false">Inactive only</option>
            </select>
          </div>
          <div className="lg:w-44">
            <label htmlFor="signal" className="block text-sm font-medium text-ink-600 mb-1">Ad lifecycle</label>
            <select
              id="signal"
              className="input w-full"
              value={filters.signal}
              onChange={(e) => setFilters({ ...filters, signal: e.target.value, page: 1 })}
            >
              <option value="">Any</option>
              <option value="stopped_early">Stopped early</option>
              <option value="ran_to_event">Ran to event</option>
              <option value="ongoing">Still advertising</option>
            </select>
          </div>
          <div className="lg:w-44">
            <label htmlFor="from" className="block text-sm font-medium text-ink-600 mb-1">Started from</label>
            <input id="from" type="date" className="input w-full" value={startInput} onChange={(e) => setStartInput(e.target.value)} />
          </div>
          <div className="lg:w-44">
            <label htmlFor="to" className="block text-sm font-medium text-ink-600 mb-1">Started to</label>
            <input id="to" type="date" className="input w-full" value={endInput} onChange={(e) => setEndInput(e.target.value)} />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
              <input
                type="checkbox"
                checked={filters.multiOnly}
                onChange={(e) => setFilters({ ...filters, multiOnly: e.target.checked, page: 1 })}
                className="rounded text-dental-600 focus:ring-dental-500"
              />
              <span className="text-sm text-ink-700">Multiple ads only</span>
            </label>
            <button type="button" onClick={applyFilters} disabled={loading} className="btn-primary whitespace-nowrap">
              {loading ? 'Loading…' : 'Apply Filters'}
            </button>
          </div>
        </div>
      </div>

      {enrichNote && (
        <div className={`card ${enrichNote.tone === 'error' ? 'bg-red-50 ring-red-200' : 'bg-dental-50 ring-dental-200'}`}>
          <p className={`text-sm ${enrichNote.tone === 'error' ? 'text-red-700' : 'text-dental-800'}`}>
            {enrichNote.text}
          </p>
        </div>
      )}

      {error && (
        <div className="card bg-red-50 ring-red-200">
          <p className="text-red-700">{error}</p>
          <button onClick={() => setFilters({ ...filters })} className="btn-secondary mt-2">Try again</button>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : events.length === 0 ? (
        <EmptyState icon={BarChart3} title="No ads match" message="Try clearing filters or widening the date range." />
      ) : (
        <div className="card p-0">
          <div className="overflow-x-auto rounded-2xl">
            <table className="w-full min-w-[900px] text-sm border-collapse">
              <thead>
                <tr className="border-b border-ink-200 bg-ink-50">
                  <th className="px-3 py-2.5 w-8" />
                  <th className="px-3 py-2.5 font-semibold text-ink-600 text-left">Event</th>
                  <th className="px-3 py-2.5 font-semibold text-ink-600 text-left whitespace-nowrap">Clinic</th>
                  <th className="px-3 py-2.5 font-semibold text-ink-600 text-left whitespace-nowrap">Location</th>
                  <SortHeader sortKey="recent" label="Started" />
                  <SortHeader
                    sortKey="duration"
                    label="Ran for"
                    title="Median of this event's finished ads. Ads still running are excluded — their span is still growing. Open the row to see each ad."
                  />
                  <SortHeader sortKey="ads" label="Ads" />
                  <SortHeader
                    sortKey="reach"
                    label="Reach"
                    title="Exact people reached where the ad has been enriched; otherwise Facebook's coarse impressions band."
                  />
                </tr>
              </thead>
              <tbody>
                {events.map((ev) => {
                  const isOpen = expanded.has(ev.id);
                  return (
                    <Fragment key={ev.id}>
                      <tr
                        onClick={() => toggleExpand(ev.id)}
                        className="border-b border-ink-100 hover:bg-dental-50/40 cursor-pointer transition-colors"
                      >
                        <td className="px-3 py-2.5 text-ink-400">
                          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </td>
                        <td className="px-3 py-2.5 font-medium text-ink-900">
                          <span className="line-clamp-1">{ev.eventTitle || 'Untitled event'}</span>
                          <span className="flex flex-wrap items-center gap-1 mt-1">
                            {ev.offer && <span className="badge badge-success text-[11px]">{ev.offer}</span>}
                            {ev.lifecycleSignal && ev.lifecycleSignal !== 'unknown' && (
                              <span
                                title={ev.lifecycleLabel}
                                className={`badge text-[11px] ${SIGNALS[ev.lifecycleSignal]?.cls || ''}`}
                              >
                                {SIGNALS[ev.lifecycleSignal]?.label || ev.lifecycleSignal}
                              </span>
                            )}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-ink-600 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5 text-ink-400 shrink-0" />
                            <span className="line-clamp-1 max-w-[180px]">{ev.clinicName || '—'}</span>
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-ink-600 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 text-ink-400 shrink-0" />
                            {ev.location || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right whitespace-nowrap text-ink-600">{formatDate(ev.firstStarted)}</td>
                        <td
                          className="px-3 py-2.5 text-right whitespace-nowrap text-ink-700 tabular-nums"
                          title={ev.durationSummary?.medianDays != null
                            ? `Median of ${ev.durationSummary.endedCount} finished ad${ev.durationSummary.endedCount === 1 ? '' : 's'}`
                              + (ev.durationSummary.endedCount > 1 ? ` (${ev.durationSummary.minDays}–${ev.durationSummary.maxDays} days)` : '')
                              + (ev.durationSummary.ongoingCount ? `, ${ev.durationSummary.ongoingCount} still running and not counted` : '')
                            : 'No finished ad yet'}
                        >
                          {ev.durationSummary?.medianDays != null
                            ? formatDuration(ev.durationSummary.medianDays)
                            : ev.durationSummary?.ongoingCount
                              ? <span className="text-ink-400">running</span>
                              : <span className="text-ink-300">—</span>}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold tabular-nums ${ev.adCount > 1 ? 'bg-dental-100 text-dental-700' : 'bg-ink-100 text-ink-500'}`}>
                            {ev.adCount}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-ink-800">
                          {ev.reachLabel || <span className="text-ink-300">—</span>}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-ink-50/50">
                          <td />
                          <td colSpan={7} className="px-3 pb-3 space-y-3">
                            {/* Audience — only exists once the ads have been enriched. */}
                            <div className="rounded-xl ring-1 ring-ink-200 bg-white p-3">
                              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                <span className="inline-flex items-center gap-1.5 font-semibold text-ink-700">
                                  <Target className="w-4 h-4 text-dental-600" />
                                  Audience &amp; targeting
                                  <span className="font-normal text-ink-400">
                                    ({ev.enrichedAdCount}/{ev.adCount} ads fetched)
                                  </span>
                                </span>
                                <span className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      runEnrich(ev.id, { eventId: ev.id, force: true }, ev.eventTitle || 'Event');
                                    }}
                                    disabled={enriching.has(ev.id)}
                                    className="btn-secondary text-xs py-1 px-2 inline-flex items-center gap-1.5"
                                    title={`Fetches all ${ev.adCount} ad${ev.adCount === 1 ? '' : 's'} for this event — 1 API credit each`}
                                  >
                                    <RefreshCw className={`w-3.5 h-3.5 ${enriching.has(ev.id) ? 'animate-spin' : ''}`} />
                                    {enriching.has(ev.id) ? 'Updating…' : 'Update this event'}
                                  </button>
                                  {ev.clinicName && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        runEnrich(`clinic:${ev.clinicName}`, { clinic: ev.clinicName, force: true }, ev.clinicName);
                                      }}
                                      disabled={enriching.has(`clinic:${ev.clinicName}`)}
                                      className="btn-secondary text-xs py-1 px-2 inline-flex items-center gap-1.5"
                                      title="Fetches every ad from this clinic — 1 API credit each"
                                    >
                                      <Building2 className={`w-3.5 h-3.5 ${enriching.has(`clinic:${ev.clinicName}`) ? 'animate-pulse' : ''}`} />
                                      Whole clinic
                                    </button>
                                  )}
                                </span>
                              </div>

                              {ev.audience ? (
                                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                                  {ev.estimatedSpend && (
                                    <div className="sm:col-span-2 lg:col-span-4 order-last pt-2 border-t border-ink-100">
                                      <span className="inline-flex items-center gap-1.5 text-ink-700">
                                        <Coins className="w-3.5 h-3.5 text-amber-500" />
                                        Estimated spend{' '}
                                        <strong>
                                          {ev.estimatedSpend.currency === 'GBP' ? '£' : ''}
                                          {ev.estimatedSpend.min.toLocaleString()}–
                                          {ev.estimatedSpend.currency === 'GBP' ? '£' : ''}
                                          {ev.estimatedSpend.max.toLocaleString()}
                                        </strong>
                                      </span>
                                      <span className="text-ink-400">
                                        {' '}— from the largest ad&apos;s reach at{' '}
                                        {summary?.cpm?.minCpm}–{summary?.cpm?.maxCpm} CPM. An estimate:
                                        the Ad Library reports no spend at all.
                                      </span>
                                    </div>
                                  )}
                                  <div>
                                    <p className="text-ink-500 mb-0.5">People reached</p>
                                    <p
                                      className="text-base font-semibold text-ink-900 tabular-nums"
                                      title={ev.audience.adsWithAudience > 1
                                        ? `Largest single ad. The ${ev.audience.adsWithAudience} ads add up to ${ev.audience.sumAdReach?.toLocaleString()}, but they target the same places, so the same people are counted more than once.`
                                        : undefined}
                                    >
                                      {ev.audience.maxAdReach?.toLocaleString() ?? '—'}
                                    </p>
                                    {ev.audience.adsWithAudience > 1 && (
                                      <p className="text-ink-400">
                                        largest of {ev.audience.adsWithAudience} ads
                                      </p>
                                    )}
                                  </div>
                                  <div>
                                    <p className="text-ink-500 mb-0.5">Targeted</p>
                                    <p className="text-ink-800">
                                      {ev.audience.ageMin ?? '?'}–{ev.audience.ageMax ?? '?'}
                                      {ev.audience.gender ? ` · ${ev.audience.gender}` : ''}
                                    </p>
                                    {ev.audience.demographics?.topAgeRange && (
                                      <p className="text-ink-400">
                                        biggest band {ev.audience.demographics.topAgeRange}
                                      </p>
                                    )}
                                  </div>
                                  <div>
                                    <p className="text-ink-500 mb-0.5">Cities</p>
                                    <p className="text-ink-800">
                                      {ev.audience.locationLabels?.length
                                        ? ev.audience.locationLabels.join(' · ')
                                        : '—'}
                                    </p>
                                    {ev.audience.excludedLocationLabels?.length > 0 && (
                                      <p className="text-ink-400">
                                        excluded: {ev.audience.excludedLocationLabels.join(' · ')}
                                      </p>
                                    )}
                                  </div>
                                  <div>
                                    <p className="text-ink-500 mb-1">Age split</p>
                                    {ev.audience.demographics?.byAge?.length ? (
                                      <div className="space-y-0.5">
                                        {ev.audience.demographics.byAge.map((a) => (
                                          <div key={a.ageRange} className="flex items-center gap-1.5">
                                            <span className="w-12 shrink-0 text-ink-600 tabular-nums">{a.ageRange}</span>
                                            <span className="flex-1 h-1.5 rounded-full bg-ink-100 overflow-hidden">
                                              <span
                                                className="block h-full bg-dental-500"
                                                style={{ width: `${Math.min(100, a.share * 2.5)}%` }}
                                              />
                                            </span>
                                            <span className="w-10 text-right text-ink-500 tabular-nums">{a.share}%</span>
                                          </div>
                                        ))}
                                        <p className="text-ink-400 pt-0.5">
                                          {ev.audience.demographics.byGender.maleShare}% male ·{' '}
                                          {ev.audience.demographics.byGender.femaleShare}% female
                                          {(() => {
                                            const g = ev.audience.demographics.byGender;
                                            const rest = Math.round((100 - g.maleShare - g.femaleShare) * 10) / 10;
                                            return rest > 0.1 ? ` · ${rest}% unknown` : '';
                                          })()}
                                        </p>
                                      </div>
                                    ) : <p className="text-ink-300">—</p>}
                                  </div>
                                </div>
                              ) : (
                                <p className="text-xs text-ink-400">
                                  Not fetched yet. Facebook only returns targeting, age/gender reach and
                                  concrete audience size from the per-ad endpoint, so it has to be
                                  requested per ad.
                                </p>
                              )}
                            </div>

                            <div className="rounded-xl ring-1 ring-ink-200 bg-white overflow-hidden">
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b border-ink-100 text-ink-500">
                                    <th className="px-3 py-2 text-left font-medium">Page</th>
                                    <th className="px-3 py-2 text-left font-medium">Started</th>
                                    <th className="px-3 py-2 text-left font-medium">Ran for</th>
                                    <th className="px-3 py-2 text-left font-medium">Status</th>
                                    <th className="px-3 py-2 text-right font-medium">Reach</th>
                                    <th className="px-3 py-2 text-right font-medium">Link</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {ev.ads.map((ad) => (
                                    <tr key={ad.adArchiveId} className="border-b border-ink-50 last:border-0">
                                      <td className="px-3 py-2 text-ink-700">
                                        <span className="line-clamp-1 max-w-[220px]">{ad.pageName || '—'}</span>
                                      </td>
                                      <td className="px-3 py-2 text-ink-600 whitespace-nowrap">{formatDate(ad.startDate)}</td>
                                      <td className="px-3 py-2 whitespace-nowrap">
                                        <span className={ad.duration?.source === 'unknown' ? 'text-ink-300' : 'text-ink-700'}>
                                          {ad.duration?.label || '—'}
                                        </span>
                                        {/* A short run means nothing on its own if another ad took over. */}
                                        {ad.wasReplaced && (
                                          <span className="badge bg-ink-100 text-ink-500 text-[10px] ml-1.5" title="Another ad for this event started when this one stopped">
                                            replaced
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2">
                                        <span className={`badge text-[10px] ${ad.isActive ? 'badge-success' : 'bg-ink-100 text-ink-500'}`}>
                                          {ad.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                      </td>
                                      <td className={`px-3 py-2 text-right tabular-nums whitespace-nowrap ${reachTone(ad.reach.source)}`}>
                                        {ad.reach.label}
                                      </td>
                                      <td className="px-3 py-2 text-right">
                                        {ad.adUrl ? (
                                          <a
                                            href={ad.adUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
                                          >
                                            View Ad <ExternalLink className="w-3 h-3" />
                                          </a>
                                        ) : <span className="text-ink-300">—</span>}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            {/* What the clinic asks prospects for, and where it
                                sends them — both come from the ad's lead form. */}
                            {(() => {
                              const questions = [...new Set(ev.ads.flatMap((a) => a.formQuestions || []))];
                              const links = [...new Set(ev.ads.flatMap((a) => a.extraLinks || []))];
                              if (questions.length === 0 && links.length === 0) return null;
                              return (
                                <div className="rounded-xl ring-1 ring-ink-200 bg-white p-3 text-xs">
                                  {questions.length > 0 && (
                                    <>
                                      <p className="font-semibold text-ink-700 mb-1.5">Lead form asks for</p>
                                      <div className="flex flex-wrap gap-1.5 mb-2">
                                        {questions.map((q) => (
                                          <span key={q} className="badge bg-ink-100 text-ink-600 text-[11px] font-normal">{q}</span>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                  {links.length > 0 && (
                                    <>
                                      <p className="font-semibold text-ink-700 mb-1.5">Links</p>
                                      <div className="flex flex-wrap gap-2">
                                        {links.map((l) => (
                                          <a
                                            key={l}
                                            href={l}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
                                          >
                                            {l} <ExternalLink className="w-3 h-3" />
                                          </a>
                                        ))}
                                      </div>
                                    </>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
          <button
            onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
            disabled={filters.page === 1}
            className="btn-secondary disabled:opacity-50 w-full sm:w-auto"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-xs sm:text-sm whitespace-nowrap">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
            disabled={filters.page >= pagination.totalPages}
            className="btn-secondary disabled:opacity-50 w-full sm:w-auto"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default AdStats;
